import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { listFarms } from "@/api/farms";
import { localizedName } from "@/lib/localizedField";
import { useCapability, useHasTenant } from "@/rbac/useCapability";
import { ChevronIcon } from "./icons";

// Farm creation happens inside the Farm Console (map-native draw/upload), not
// on the legacy /farms/new form. Query param rather than a path segment: goTo()
// below rewrites the LAST path segment on a farm switch, which would mangle a
// /labs/map/new route. See docs/proposals/farm-creation-in-console.md.
const NEW_FARM_PATH = "/labs/map-v2?create=farm";

const PINNED_PREFIXES = [
  "/insights/",
  "/board/",
  "/timeline/",
  "/plan/",
  "/alerts/",
  "/reports/",
  "/config/",
  "/labs/map/",
  "/labs/map-next/",
  "/labs/map-legacy/",
  "/labs/map-v2/",
];

/**
 * Breadcrumb-anchored farm switcher. Keeps the user on the current view
 * but swaps the `:farmId` segment when they pick a different farm.
 *
 * Renders nothing without a tenant. The /platform pages share this header,
 * and `/v1/farms` answers a platform admin with no tenant with a 403.
 */
export function FarmSwitcher(): ReactNode {
  const hasTenant = useHasTenant();
  return hasTenant ? <TenantFarmSwitcher /> : null;
}

function TenantFarmSwitcher(): ReactNode {
  const { farmId } = useParams<{ farmId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("common");
  const canCreateFarm = useCapability("farm.create");
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { data: page } = useQuery({
    queryKey: ["farms", "list", "switcher"] as const,
    queryFn: () => listFarms(),
    staleTime: 60_000,
  });
  const farms = page?.items ?? [];
  const active = farms.find((f) => f.id === farmId);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !buttonRef.current?.contains(e.target as Node) &&
        !popoverRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const goTo = (id: string): void => {
    // If the current pathname has a farm segment we recognise, swap it.
    // Otherwise default to /insights/<id>.
    let nextPath = `/insights/${id}`;
    // Investments pages keep their section. A holding or investor page goes
    // to its section's list: that holding or investor may not be on the new
    // farm.
    const investments = /^\/investments\/(overview|holdings|investors)\b/.exec(location.pathname);
    if (investments) {
      setOpen(false);
      navigate(`/investments/${investments[1]}/${id}`);
      return;
    }
    for (const prefix of PINNED_PREFIXES) {
      if (location.pathname.startsWith(prefix)) {
        // Replace the segment after the prefix (handles /config/rules/<id>).
        const segments = location.pathname.split("/");
        // Last segment is the farmId for our current pinned routes.
        if (segments.length >= 2) {
          segments[segments.length - 1] = id;
          nextPath = segments.join("/");
        }
        break;
      }
    }
    setOpen(false);
    navigate(nextPath);
  };

  // The farm switcher is tenant-context UI. On the platform portal
  // (/platform/*) a PlatformAdmin has no farms to manage, so the
  // "create your first farm" CTA is pointless there — render nothing.
  if (location.pathname.startsWith("/platform")) {
    return null;
  }

  if (farms.length === 0) {
    if (canCreateFarm) {
      return (
        <button
          type="button"
          onClick={() => navigate(NEW_FARM_PATH)}
          className="rounded-md bg-ap-primary px-2 py-1 text-sm font-medium text-white hover:bg-ap-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ap-primary"
        >
          {t("home.createFirstFarm")}
        </button>
      );
    }
    return <span className="text-sm text-ap-muted">{t("home.noFarmsTitle")}</span>;
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-ap-ink hover:bg-ap-line/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ap-primary"
      >
        <span className="truncate" dir="auto">
          {active
            ? localizedName(i18n.language, active.name, active.name_ar)
            : t("farmSwitcher.placeholder")}
        </span>
        <ChevronIcon className="h-3 w-3 rotate-90" />
      </button>
      {open ? (
        <div
          ref={popoverRef}
          role="listbox"
          /* eslint-disable-next-line no-restricted-syntax --
             A floating listbox, not a page panel: it needs its own radius,
             elevation and z-index, none of which <Card> models. */
          className="absolute start-0 top-full z-40 mt-1 max-h-80 w-64 overflow-auto rounded-lg border border-ap-line bg-ap-panel p-1 shadow-card"
        >
          {farms.map((f) => {
            const selected = f.id === farmId;
            return (
              <button
                type="button"
                key={f.id}
                role="option"
                aria-selected={selected}
                onClick={() => goTo(f.id)}
                className={`flex w-full flex-col items-start gap-0.5 rounded-md px-3 py-2 text-start text-sm hover:bg-ap-line/40 ${
                  selected ? "bg-ap-primary-soft" : ""
                }`}
              >
                <span className="font-medium text-ap-ink" dir="auto">
                  {localizedName(i18n.language, f.name, f.name_ar)}
                </span>
                <span className="text-[11px] text-ap-muted">{f.code}</span>
              </button>
            );
          })}
          {canCreateFarm ? (
            <>
              <div role="separator" className="my-1 border-t border-ap-line" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(NEW_FARM_PATH);
                }}
                className="flex w-full items-center rounded-md px-3 py-2 text-start text-sm font-medium text-ap-primary hover:bg-ap-line/40"
              >
                {t("farmSwitcher.newFarm")}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
